/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { widgetsDuplicate } from "../funcs/widgetsDuplicate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Widget } from "../models/components/widget.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DuplicateWidgetRequest,
  DuplicateWidgetSecurity,
} from "../models/operations/duplicatewidget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DuplicateWidgetMutationVariables = {
  request: DuplicateWidgetRequest;
  security?: DuplicateWidgetSecurity | undefined;
  options?: RequestOptions;
};

export type DuplicateWidgetMutationData = Widget;

export type DuplicateWidgetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * duplicateWidget widgets
 *
 * @remarks
 * Copy a widget into a new one the caller owns, named "<name> (copy)". This is how a widget is shared: a teammate duplicates it rather than editing it. Like every save, the copy is validated, so a broken widget cannot be duplicated until it is fixed.
 */
export function useDuplicateWidgetMutation(
  options?: MutationHookOptions<
    DuplicateWidgetMutationData,
    DuplicateWidgetMutationError,
    DuplicateWidgetMutationVariables
  >,
): UseMutationResult<
  DuplicateWidgetMutationData,
  DuplicateWidgetMutationError,
  DuplicateWidgetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDuplicateWidgetMutation(client, options),
    ...options,
  });
}

export function mutationKeyDuplicateWidget(): MutationKey {
  return ["@gram/client", "widgets", "duplicate"];
}

export function buildDuplicateWidgetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DuplicateWidgetMutationVariables,
  ) => Promise<DuplicateWidgetMutationData>;
} {
  return {
    mutationKey: mutationKeyDuplicateWidget(),
    mutationFn: function duplicateWidgetMutationFn({
      request,
      security,
      options,
    }): Promise<DuplicateWidgetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(widgetsDuplicate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
